// Modal "Análises" do caderno — Fase 1 do roadmap NotebookLM.
// 5 tipos textuais: briefing | summary | glossary | study_guide | faq
// + Audio Overview (gera script narrado + TTS).
//
// [[project-caderno-notebooklm-roadmap]]

import { useEffect, useState } from "preact/hooks";
import {
  BookOpen,
  FileText,
  GraduationCap,
  HelpCircle,
  Headphones,
  Library,
  Loader2,
  RefreshCw,
  Sparkles,
  X
} from "lucide-react";
import { api } from "./api";
import { AiMarkdownBlock } from "./aiMarkdown";
import { friendlyError } from "@app/ui/lib/friendly-error";

type AnalysisKind = "briefing" | "summary" | "glossary" | "study_guide" | "faq" | "audio_overview";

interface AnalysisRow {
  id: string;
  kind: string;
  content: string;
  audioDataUrl: string | null;
  model: string | null;
  provider: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Tab {
  kind: AnalysisKind;
  label: string;
  icon: typeof BookOpen;
  desc: string;
  isAudio?: boolean;
}

const TABS: Tab[] = [
  { kind: "briefing", label: "Briefing", icon: Sparkles, desc: "1 página — visão executiva" },
  { kind: "summary", label: "Resumo", icon: BookOpen, desc: "Resumo detalhado (~10 min)" },
  { kind: "study_guide", label: "Roteiro", icon: GraduationCap, desc: "Por onde começar e como autoavaliar" },
  { kind: "glossary", label: "Glossário", icon: Library, desc: "Termos técnicos do material" },
  { kind: "faq", label: "FAQ", icon: HelpCircle, desc: "Perguntas frequentes" },
  { kind: "audio_overview", label: "Áudio Overview", icon: Headphones, desc: "Resumo narrado em áudio", isAudio: true }
];

export function CadernoAnalysisModal({
  sessionId,
  sessionTitle,
  hasDocuments,
  onClose
}: {
  sessionId: string;
  sessionTitle: string;
  hasDocuments: boolean;
  onClose: () => void;
}) {
  const [active, setActive] = useState<AnalysisKind>("briefing");
  const [items, setItems] = useState<Map<string, AnalysisRow>>(new Map());
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState<AnalysisKind | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.listAiStudyAnalyses(sessionId);
        if (!alive) return;
        const m = new Map<string, AnalysisRow>();
        for (const a of r.analyses) m.set(a.kind, a);
        setItems(m);
      } catch (e) {
        if (alive) setErr(friendlyError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [sessionId]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function doGenerate(kind: AnalysisKind) {
    setErr(null);
    setGenerating(kind);
    try {
      if (kind === "audio_overview") {
        const r = await api.generateAiStudyAudioOverview(sessionId);
        setItems((prev) => {
          const next = new Map(prev);
          next.set(kind, {
            id: r.id,
            kind,
            content: r.content,
            audioDataUrl: r.audioDataUrl,
            model: null,
            provider: null,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          });
          return next;
        });
      } else {
        const r = await api.generateAiStudyAnalysis(sessionId, kind);
        setItems((prev) => {
          const next = new Map(prev);
          next.set(kind, {
            id: r.id,
            kind,
            content: r.content,
            audioDataUrl: null,
            model: r.model,
            provider: r.provider,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          });
          return next;
        });
      }
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setGenerating(null);
    }
  }

  const current = items.get(active) ?? null;
  const tab = TABS.find((t) => t.kind === active)!;

  return (
    <div
      class="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-3"
      role="dialog"
      aria-modal="true"
      aria-label="Análises do caderno"
    >
      <div class="flex h-[90vh] max-h-[800px] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl">
        {/* Header */}
        <div class="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <div class="text-xs font-bold uppercase tracking-wider text-violet-600 dark:text-violet-400">
              Análises do caderno
            </div>
            <div class="truncate text-sm font-semibold text-foreground">{sessionTitle}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            class="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent"
            aria-label="Fechar"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Tabs */}
        <div class="flex shrink-0 gap-1 overflow-x-auto border-b border-border px-3 py-2">
          {TABS.map((t) => {
            const Icon = t.icon;
            const has = items.has(t.kind);
            const isActive = active === t.kind;
            return (
              <button
                key={t.kind}
                type="button"
                onClick={() => setActive(t.kind)}
                class={`inline-flex shrink-0 items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  isActive
                    ? "border-violet-500/40 bg-violet-500/10 text-violet-700 dark:text-violet-300"
                    : "border-transparent text-muted-foreground hover:bg-accent"
                }`}
              >
                <Icon className="size-3.5" />
                {t.label}
                {has && (
                  <span
                    class="ml-0.5 inline-block size-1.5 rounded-full bg-emerald-500"
                    title="Já gerado"
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div class="flex-1 overflow-y-auto">
          {!hasDocuments ? (
            <div class="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
              <FileText className="size-12 text-muted-foreground/40" />
              <div class="text-sm text-muted-foreground">
                Anexe ao menos um documento (PDF, TXT ou URL) ao caderno antes de gerar análises.
              </div>
            </div>
          ) : loading ? (
            <div class="flex h-full items-center justify-center">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          ) : !current ? (
            <div class="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
              <tab.icon className="size-12 text-violet-500/60" />
              <div>
                <div class="text-base font-semibold text-foreground">{tab.label}</div>
                <div class="mt-1 text-xs text-muted-foreground">{tab.desc}</div>
              </div>
              <button
                type="button"
                disabled={generating !== null}
                onClick={() => void doGenerate(active)}
                class="inline-flex items-center gap-2 rounded-md bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
              >
                {generating === active ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Gerando…
                  </>
                ) : (
                  <>
                    <Sparkles className="size-4" />
                    Gerar {tab.label.toLowerCase()}
                  </>
                )}
              </button>
              {tab.isAudio && (
                <div class="max-w-md text-[11px] text-muted-foreground">
                  O áudio é gerado por TTS da OpenAI (~3-5 min de narração).
                </div>
              )}
            </div>
          ) : (
            <div class="flex flex-col gap-3 p-5">
              <div class="flex items-center justify-between">
                <div class="text-xs text-muted-foreground">
                  Gerado em {new Date(current.updatedAt).toLocaleString("pt-BR")}
                  {current.provider && current.model && (
                    <span class="ml-2 opacity-60">
                      ({current.provider} · {current.model})
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  disabled={generating !== null}
                  onClick={() => void doGenerate(active)}
                  class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 text-[11px] font-semibold hover:bg-accent disabled:opacity-50"
                  title="Regenerar com base nas fontes atuais"
                >
                  {generating === active ? (
                    <Loader2 className="size-3 animate-spin" />
                  ) : (
                    <RefreshCw className="size-3" />
                  )}
                  Regenerar
                </button>
              </div>
              {tab.isAudio && current.audioDataUrl && (
                <audio
                  controls
                  src={current.audioDataUrl}
                  class="w-full"
                  preload="metadata"
                />
              )}
              <AiMarkdownBlock text={current.content} className="prose prose-sm max-w-none dark:prose-invert" />
            </div>
          )}
        </div>

        {err && (
          <div class="border-t border-destructive/30 bg-destructive/10 px-4 py-2 text-xs text-destructive">
            {err}
          </div>
        )}
      </div>
    </div>
  );
}
